import { onMounted, onBeforeUnmount } from 'vue';

/**
 * Hide everything outside a dialog from assistive technology.
 *
 * Safari/VoiceOver stops exposing the non-focusable text inside a container
 * marked `aria-modal="true"`, which leaves dialog body copy unreadable. Dialogs
 * therefore drop aria-modal and isolate themselves the way the ARIA dialog
 * pattern did before it existed: mark every sibling on the path from the dialog
 * up to <body> with aria-hidden for as long as the dialog is mounted.
 *
 * The siblings also get `inert`, which drops their whole subtree out of the Tab
 * order as well as the accessibility tree. Without it, focusable controls behind
 * the dialog stay tabbable inside an aria-hidden region.
 *
 * @param {import('vue').Ref<HTMLElement|null>} rootRef Ref to the dialog root.
 */
export function useAriaHiddenBackground(rootRef) {
  const hiddenElements = [];
  const inertedElements = [];

  onMounted(() => {
    let node = rootRef.value;

    while (node && node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node) {
          continue;
        }

        if (!sibling.hasAttribute('aria-hidden')) {
          sibling.setAttribute('aria-hidden', 'true');
          hiddenElements.push(sibling);
        }

        if (!sibling.inert) {
          sibling.inert = true;
          inertedElements.push(sibling);
        }
      }

      node = node.parentElement;
    }
  });

  onBeforeUnmount(() => {
    hiddenElements.forEach(element => element.removeAttribute('aria-hidden'));
    hiddenElements.length = 0;

    inertedElements.forEach(element => {
      element.inert = false;
    });
    inertedElements.length = 0;
  });
}
